import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import RadioButtonField from 'components/RadioGroupField/RadioButtonField';
import SelectField from 'components/SelectField';
import TrashIcon from 'icons/Trash.icon';
import {
  SortByForm,
  SORTING_ORDER,
  SortingDirectionValues,
  SortingField,
} from 'models/Sort';
import { Button } from 'ncoded-component-library';
import { Field, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { Option } from 'models/Global';
import PlusIcon from 'icons/Plus.icon';
import { getArrayLength, isSingleElementArray, sortOptions } from 'utils';
import { SMOOTH_SCROLL_CONFIGURATION } from 'data/global';
import { FocusFieldRef } from 'models/FieldFocusableModal';

import './MultiSortOptions.styles.scss';

export const INITIAL_VALUE = {
  name: '',
  order: SORTING_ORDER.A_TO_Z,
} as SortingField;

export type MultiSortOptionsProps = {
  className?: string;
  options: Option[];
  sortDirectionTranslations?: SortingDirectionValues;
  // Set to false to keep the declared option order, e.g. to mirror the table's
  // column order instead of listing them alphabetically.
  shouldSortOptions?: boolean;
};

const MultiSortOptions: React.ForwardRefRenderFunction<
  FocusFieldRef,
  MultiSortOptionsProps
> = (props, ref) => {
  const {
    className,
    options,
    sortDirectionTranslations,
    shouldSortOptions = true,
  } = props;

  const rowRefs = useRef(new Map<string, HTMLDivElement>());

  useImperativeHandle(
    ref,
    () => ({
      focusField: (fieldKey: string) => {
        rowRefs.current
          .get(fieldKey)
          ?.scrollIntoView({ ...SMOOTH_SCROLL_CONFIGURATION, block: 'center' });
      },
    }),
    [],
  );

  const classes = classNames(
    'multi-sort-options',
    'multi-fields-options',
    className,
  );

  const { t } = useTranslation();

  const {
    values: { sortBy },
  } = useFormState<SortByForm>();

  const sortedOptions = useMemo(
    () => (shouldSortOptions ? sortOptions(options) : options),
    [options, shouldSortOptions],
  );

  const getRowRef = useCallback(
    (sortName: string) => (el: HTMLDivElement | null) => {
      if (!sortName) return;
      if (el) rowRefs.current.set(sortName, el);
      else rowRefs.current.delete(sortName);
    },
    [],
  );

  return (
    <div className={classes}>
      <FieldArray name="sortBy">
        {({ fields }) => (
          <>
            <div className="multi-sort-options__rows">
              {fields.map((name, index) => {
                const isFirstElement = index === 0;

                const { name: sortName } = fields.value[index] || {};

                const sortDirectionTranslation =
                  sortDirectionTranslations?.[sortName];

                return (
                  <div key={name} className="row" ref={getRowRef(sortName)}>
                    <div>
                      <p>{t(isFirstElement ? 'sortBy' : 'thenBy')}</p>

                      <Field
                        name={`${name}.name`}
                        component={SelectField}
                        renderAsPortal
                        portalClassName="multi-sort-select"
                        options={sortedOptions.map((el) => ({
                          ...el,
                          disabled: sortBy.find(
                            ({ name }) => name === el.value,
                          ),
                        }))}
                      />
                    </div>

                    <div>
                      {sortName && (
                        <>
                          <Field
                            name={`${name}.order`}
                            type="radio"
                            value={SORTING_ORDER.A_TO_Z as string}
                            component={RadioButtonField}
                            label={t(
                              sortDirectionTranslation?.a_to_z || 'aToZ',
                            )}
                          />
                          <Field
                            name={`${name}.order`}
                            type="radio"
                            value={SORTING_ORDER.Z_TO_A as string}
                            component={RadioButtonField}
                            label={t(
                              sortDirectionTranslation?.z_to_a || 'zToA',
                            )}
                          />
                        </>
                      )}

                      {!isSingleElementArray(fields?.value) ? (
                        <Button
                          variant="icon"
                          className="warning-action-button"
                          icon={TrashIcon}
                          onClick={() => fields.remove(index)}
                        />
                      ) : (
                        <div className="button-placeholder" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {fields.length < getArrayLength(options) && (
              <div className="multi-sort-options__footer">
                <Button
                  icon={PlusIcon}
                  variant="outline"
                  onClick={() => fields.push(INITIAL_VALUE)}
                >
                  {t('addSort')}
                </Button>
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default forwardRef(MultiSortOptions);
